import React, { useState } from 'react';
import './ShoppingList.css';
import { getCategoryGradient, formatVariant, logComparisonViewed } from '../utils/db';
import { getSubcategoryDisplay } from '../utils/taxonomy';
import { trackCompareOpen } from '../utils/analytics';
import StoreCompareSheet from './StoreCompareSheet';

const TIER_LABELS = { basic: 'Value', standard: 'Standard', premium: 'Premium', organic: 'Organic' };
const formatTier = (tier) => TIER_LABELS[tier?.toLowerCase()] || null;

function ShoppingList({ items, stores, onRemove, onUpdateQuantity, onSave, onClear, onOpenPenny, onSelectProduct }) {
  const [confirmedStoreId, setConfirmedStoreId] = useState(null);
  const [showStorePicker, setShowStorePicker]   = useState(false);
  const [showChecklist, setShowChecklist]       = useState(false);
  const [previewStoreId, setPreviewStoreId]     = useState(null);
  const [showCompare, setShowCompare]           = useState(false);

  // Get cheapest price for an item across all stores
  const getCheapestItemPrice = (item) => {
    if (!item.prices?.length) return null;
    const valid = item.prices.filter(p => p.price > 0);
    if (!valid.length) return null;
    return Math.min(...valid.map(p => p.price));
  };

  // Get price for a specific store (normalises store_tesco → tesco)
  const getStoreItemPrice = (item, storeId) => {
    if (!item.prices?.length) return null;
    const price = item.prices.find(p => {
      const normId = (p.storeId || '').replace(/^store_/, '');
      return normId === storeId || p.storeId === storeId;
    });
    return price?.price > 0 ? price.price : null;
  };

  // Returns totals, matched count, and missing item names per store
  const calculateStoreData = () => {
    const totals  = {};
    const matched = {};
    const missing = {};
    stores.forEach(store => {
      totals[store.id]  = 0;
      matched[store.id] = 0;
      missing[store.id] = [];
    });
    items.forEach(item => {
      stores.forEach(store => {
        const price = getStoreItemPrice(item, store.id);
        if (price != null) {
          totals[store.id]  += price * item.quantity;
          matched[store.id] += 1;
        } else {
          missing[store.id].push(item.name);
        }
      });
    });
    return { totals, matched, missing };
  };

  // Only stores with 100% coverage can be declared cheapest
  const findCheapestStore = (totals, matched) => {
    let cheapestStoreId = null;
    let cheapestTotal   = Infinity;
    Object.keys(totals).forEach(storeId => {
      const isFull = items.length > 0 && matched[storeId] === items.length;
      if (isFull && totals[storeId] > 0 && totals[storeId] < cheapestTotal) {
        cheapestTotal   = totals[storeId];
        cheapestStoreId = storeId;
      }
    });
    return cheapestStoreId;
  };

  const { totals, matched, missing } = calculateStoreData();
  const cheapestStoreId = findCheapestStore(totals, matched);
  const cheapestTotal   = cheapestStoreId ? totals[cheapestStoreId] : 0;
  // savings only meaningful across full-coverage stores
  const fullTotals      = items.length > 0
    ? Object.keys(totals).filter(sid => matched[sid] === items.length && totals[sid] > 0).map(sid => totals[sid])
    : [];
  const mostExpensive   = fullTotals.length > 0 ? Math.max(...fullTotals) : 0;
  const savings         = cheapestStoreId ? mostExpensive - cheapestTotal : 0;

  // Pre-sorted store groups for the comparison UI
  const completeStores = items.length > 0
    ? stores.filter(s => matched[s.id] === items.length && totals[s.id] > 0)
            .sort((a, b) => totals[a.id] - totals[b.id])
    : [];
  const partialStores = items.length > 0
    ? stores.filter(s => matched[s.id] < items.length && totals[s.id] > 0)
            .sort((a, b) => (matched[b.id] - matched[a.id]) || (totals[a.id] - totals[b.id]))
    : [];

  // Default suggestion before confirmation — tied explicitly to complete-store state
  // (never silently default to a partial or arbitrary store)
  const defaultStoreId =
    completeStores.length === 1 ? completeStores[0].id :
    completeStores.length > 1  ? cheapestStoreId :
    null;
  const activeStoreId     = confirmedStoreId || defaultStoreId;
  const activeStoreName   = stores.find(s => s.id === activeStoreId)?.name || 'a store';
  const confirmedStore    = stores.find(s => s.id === confirmedStoreId);

  // ── Confidence tier (1–2 = low, 3–5 = medium, 6+ = high) ──────────────────
  const tier = items.length <= 2 ? 'low' : items.length <= 5 ? 'medium' : 'high';

  // Whether any store has matched any item at all
  const hasAnyStoreData = completeStores.length > 0 || partialStores.length > 0;

  // Tie detection within complete stores (only relevant when 2+ complete stores)
  const minCompleteTotal   = completeStores.length > 0
    ? Math.min(...completeStores.map(s => totals[s.id]))
    : null;
  const tiedCompleteStores = minCompleteTotal !== null
    ? completeStores.filter(s => totals[s.id] === minCompleteTotal)
    : [];
  const isTied             = tiedCompleteStores.length > 1;
  // All complete stores share the same price — list them individually would be misleading
  const allTied            = isTied && tiedCompleteStores.length === completeStores.length;
  const cheapestCompleteName = isTied
    ? tiedCompleteStores.map(s => s.name).join(' and ')
    : stores.find(s => s.id === cheapestStoreId)?.name || '';
  const cheapestVerb = isTied ? 'are' : 'is';

  // Penny headline ─ three distinct complete-store states
  const pennyMessage = confirmedStoreId
    ? `This basket is set for ${confirmedStore?.name}.`
    : !hasAnyStoreData
      ? "Add some items and I'll compare stores for you."
      : completeStores.length === 0
        ? 'No single store has your full list yet.'
        : completeStores.length === 1
          ? `${completeStores[0].name} is the only store with your full list.`
          : allTied
            ? `All stores are the same price for this list.`
            : tier === 'low'
              ? `So far, ${cheapestCompleteName} ${cheapestVerb} cheapest.`
              : tier === 'medium'
                ? `${cheapestCompleteName} ${cheapestVerb} currently the cheapest.`
                : `${cheapestCompleteName} ${cheapestVerb} the cheapest for your shop.`;

  // Penny subtext ─ coverage-aware, never "add more" when coverage is the real gap
  const pennySubtext = confirmedStoreId ? null
    : !hasAnyStoreData ? null
    : completeStores.length === 0
      ? (items.length <= 2
          ? 'Add a few more items to get a clearer comparison.'
          : 'You may need to split your shop.')
      : completeStores.length === 1 ? null
      : tier === 'low'    ? 'Add a few more items to get a clearer comparison.'
      : tier === 'medium' ? 'Add a few more items to confirm your best shop.'
      : null;

  const handleConfirmStore = (storeId) => {
    setConfirmedStoreId(storeId);
    setPreviewStoreId(null);
    setShowStorePicker(false);
  };

  return (
    <>
      <div className="shopping-list">

        {/* ── Screen orientation hint — journey cue ── */}
        <p className="screen-hint">
          {confirmedStoreId
            ? `Your list is set for ${confirmedStore?.name}`
            : 'Your list · next step: compare stores ↓'}
        </p>

        {/* ── Penny guidance card ── */}
        <div className="penny-basket-card">
          <img
            src="/Penny Fox closed eyes.png"
            alt="Penny"
            className="penny-basket-img"
          />
          <div className="penny-basket-message">
            <span className="penny-label">Penny says</span>
            <p>{pennyMessage}</p>
            {pennySubtext && (
              <span className="penny-subtext">{pennySubtext}</span>
            )}
            {confirmedStoreId && (
              <span className="penny-switch-note">You can switch stores at any time.</span>
            )}
          </div>
        </div>

        {/* ── Compact save / clear ── */}
        {(onSave || onClear) && (
          <div className="basket-actions-compact">
            {onSave && (
              <button className="basket-compact-save" onClick={onSave}>
                Save list
              </button>
            )}
            {onClear && (
              <button className="basket-compact-clear" onClick={onClear}>
                Clear
              </button>
            )}
          </div>
        )}

        {/* ── Basket items ── */}
        <div className="basket-items">
          <div className="basket-section-header">
            <span>Your items</span>
            <span className="basket-item-count">{items.length}</span>
          </div>
          {items.map(item => {
            const displayStoreId = previewStoreId || confirmedStoreId;
            const displayPrice   = displayStoreId
              ? getStoreItemPrice(item, displayStoreId)
              : getCheapestItemPrice(item);
            const isUnavailable  = !!displayStoreId && displayPrice == null;

            return (
              <div
                key={item.id}
                className={`basket-item-row${isUnavailable ? ' basket-item-row--unavailable' : ''}`}
                onClick={() => onSelectProduct && onSelectProduct(item)}
                style={{ cursor: onSelectProduct ? 'pointer' : 'default' }}
              >
                <div
                  className="basket-item-icon"
                  style={{ background: getCategoryGradient(item.category) }}
                >
                  <span>{getSubcategoryDisplay(item.subcategory).icon}</span>
                </div>

                <div className="basket-item-info">
                  <div className="basket-item-name">{item.name}</div>
                  <div className="basket-item-meta">
                    {[item.brand, formatVariant(item.variant), item.size, formatTier(item.tier)].filter(Boolean).join(' · ')}
                  </div>
                  {displayPrice != null && (
                    <div className={`basket-item-price${displayStoreId ? ' store-specific' : ''}`}>
                      {displayStoreId ? `£${displayPrice.toFixed(2)}` : `From £${displayPrice.toFixed(2)}`}
                    </div>
                  )}
                  {isUnavailable && (
                    <div className="basket-item-price no-price">Unavailable</div>
                  )}
                </div>

                <div className="basket-qty" onClick={e => e.stopPropagation()}>
                  <button
                    className="qty-btn"
                    onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
                    disabled={item.quantity <= 1}
                    aria-label="Decrease quantity"
                  >−</button>
                  <span className="qty-num">{item.quantity}</span>
                  <button
                    className="qty-btn"
                    onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                    aria-label="Increase quantity"
                  >+</button>
                </div>

                <button
                  className="basket-remove-btn"
                  onClick={e => { e.stopPropagation(); onRemove(item.id); }}
                  aria-label={`Remove ${item.name}`}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <line x1="3" y1="3" x2="11" y2="11"/>
                    <line x1="11" y1="3" x2="3" y2="11"/>
                  </svg>
                </button>
              </div>
            );
          })}
        </div>

        {/* ── Where to shop ── */}
        <div className="store-totals">
          <div className="basket-section-header">
            <span>Compare stores</span>
          </div>

          {/* Two-group store comparison — collapses after confirmation */}
          {!confirmedStoreId && (
            <>
              {/* ── Complete baskets ── */}
              {completeStores.length > 0 && (
                <div className="store-group">
                  <span className="store-group-label">Complete baskets</span>
                  {completeStores.map((store, i) => {
                    const isPreviewing = previewStoreId === store.id;
                    return (
                      <div
                        key={store.id}
                        className={`store-group-row${isPreviewing ? ' preview-selected' : ''}`}
                        onClick={() => setPreviewStoreId(prev => prev === store.id ? null : store.id)}
                      >
                        {isPreviewing && (
                          <button
                            className="store-preview-clear store-preview-clear--row"
                            onClick={e => { e.stopPropagation(); setPreviewStoreId(null); }}
                            aria-label="Close store preview"
                          >×</button>
                        )}
                        <div className="store-group-row-left">
                          <span className="store-group-row-name">{store.name}</span>
                        </div>
                        <div className="store-group-row-right">
                          <span className="coverage-chip coverage-chip--complete">{matched[store.id]}/{items.length} items</span>
                          <span className="store-group-row-price">£{totals[store.id].toFixed(2)}</span>
                          <span className="store-group-row-badge">
                            {completeStores.length > 1 && totals[store.id] === minCompleteTotal && savings > 0 && <span className="cheapest-badge">Cheapest</span>}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* ── Partial baskets ── */}
              {partialStores.length > 0 && (
                <div className="store-group">
                  <span className="store-group-label">
                    {completeStores.length === 0 ? 'Best available' : 'Partial baskets'}
                  </span>
                  {partialStores.map(store => {
                    const isPreviewing = previewStoreId === store.id;
                    return (
                      <div
                        key={store.id}
                        className={`store-group-row partial${isPreviewing ? ' preview-selected' : ''}`}
                        onClick={() => setPreviewStoreId(prev => prev === store.id ? null : store.id)}
                      >
                        {isPreviewing && (
                          <button
                            className="store-preview-clear store-preview-clear--row"
                            onClick={e => { e.stopPropagation(); setPreviewStoreId(null); }}
                            aria-label="Close store preview"
                          >×</button>
                        )}
                        <div className="store-group-row-left">
                          <span className="store-group-row-name">{store.name}</span>
                          {missing[store.id].length > 0 && (
                            <span className="store-group-row-missing">
                              Not available: {missing[store.id].join(', ')}
                            </span>
                          )}
                        </div>
                        <div className="store-group-row-right">
                          <span className="coverage-chip coverage-chip--partial">{matched[store.id]}/{items.length} items</span>
                          <span className="store-group-row-price partial">£{totals[store.id].toFixed(2)}</span>
                          <span className="store-group-row-badge" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* ── Compare stores button ── */}
              {(completeStores.length + partialStores.length) >= 2 && (
                <button
                  className="compare-stores-btn"
                  onClick={() => { trackCompareOpen(items.length); logComparisonViewed(items.length); setShowCompare(true); }}
                >
                  See item-by-item differences →
                </button>
              )}
            </>
          )}

          {/* Confirmed store row — replaces grid after confirmation */}
          {confirmedStoreId && (
            <>
              <div className="confirmed-store-row">
                <div className="confirmed-store-left">
                  <span className="confirmed-check">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="2 7 6 11 12 3"/>
                    </svg>
                  </span>
                  <span className="confirmed-store-name">{confirmedStore?.name}</span>
                </div>
                {totals[confirmedStoreId] > 0 && (
                  <span className="confirmed-store-price">
                    £{totals[confirmedStoreId].toFixed(2)}
                  </span>
                )}
                <span className="confirmed-store-badge-col">
                  {cheapestStoreId === confirmedStoreId && savings > 0 && (
                    <span className="store-picker-badge">Best price</span>
                  )}
                </span>
                <button
                  className="confirmed-change-btn"
                  onClick={() => setShowStorePicker(true)}
                >
                  Change
                </button>
              </div>
              {items.length > 0 && missing[confirmedStoreId]?.length > 0 && (
                <div className="confirmed-coverage-note">
                  {matched[confirmedStoreId]} of {items.length} items available
                  <span> · Not available: {missing[confirmedStoreId].join(', ')}</span>
                </div>
              )}
            </>
          )}

          {/* Bottom CTA section */}
          <div className="shop-at-section">
            {!confirmedStoreId ? (
              <>
                <button
                  className="shop-at-btn"
                  onClick={() => setShowStorePicker(true)}
                >
                  {activeStoreId ? `I'll shop at ${activeStoreName}` : 'Choose a store'}
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 5 7 9 11 5"/>
                  </svg>
                </button>
                <p className="shop-at-tagline">Choose a store so Penny can track your savings.</p>
                <p className="coverage-note">Product coverage is expanding — some items may appear unavailable.</p>
              </>
            ) : (
              <>
                <button
                  className="view-list-btn"
                  onClick={() => setShowChecklist(true)}
                >
                  View shopping list
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 5 7 9 11 5"/>
                  </svg>
                </button>
              </>
            )}
          </div>
        </div>

      {/* ── Prices nudge — only shown when a cheapest store has been identified ── */}
      {cheapestStoreId && onOpenPenny && (
        <div className="basket-prices-nudge">
          <button className="basket-nudge-btn" onClick={onOpenPenny}>
            Prices not quite right? Let Penny know →
          </button>
        </div>
      )}

      </div>

      {/* ── Store picker sheet ── */}
      {showStorePicker && (
        <>
          <div className="sheet-backdrop" onClick={() => setShowStorePicker(false)} />
          <div className="store-picker-sheet">
            <div className="sheet-handle" />
            <h3 className="store-picker-title">Choose your store</h3>
            {completeStores.length > 0 && (
              <p className="store-picker-group-label">Complete baskets</p>
            )}
            {completeStores.map(store => {
              const isSelected = activeStoreId === store.id;
              const isCheapest = cheapestStoreId === store.id;
              return (
                <button
                  key={store.id}
                  className={`store-picker-row${isSelected ? ' selected' : ''}`}
                  onClick={() => handleConfirmStore(store.id)}
                >
                  <div className="store-picker-left">
                    <span className="store-picker-name">{store.name}</span>
                    {isCheapest && completeStores.length > 1 && savings > 0 && (
                      <span className="store-picker-badge">Best price</span>
                    )}
                  </div>
                  <div className="store-picker-right">
                    <span className="coverage-chip">{matched[store.id]}/{items.length} items</span>
                    <span className="store-picker-price">£{totals[store.id].toFixed(2)}</span>
                    {isSelected && (
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 8 7 12 13 4"/>
                      </svg>
                    )}
                  </div>
                </button>
              );
            })}
            {partialStores.length > 0 && (
              <p className="store-picker-group-label store-picker-group-label--partial">Partial baskets</p>
            )}
            {partialStores.map(store => {
              const isSelected = activeStoreId === store.id;
              return (
                <button
                  key={store.id}
                  className={`store-picker-row${isSelected ? ' selected' : ''}`}
                  onClick={() => handleConfirmStore(store.id)}
                >
                  <div className="store-picker-left">
                    <span className="store-picker-name">{store.name}</span>
                    {missing[store.id].length > 0 && (
                      <span className="store-picker-missing">
                        Not available: {missing[store.id].join(', ')}
                      </span>
                    )}
                  </div>
                  <div className="store-picker-right">
                    <span className="coverage-chip">{matched[store.id]}/{items.length} items</span>
                    <span className="store-picker-price partial">£{totals[store.id].toFixed(2)}</span>
                    {isSelected && (
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 8 7 12 13 4"/>
                      </svg>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* ── Checklist sheet ── */}
      {showChecklist && (
        <>
          <div className="sheet-backdrop" onClick={() => setShowChecklist(false)} />
          <div className="checklist-sheet">
            <div className="sheet-handle" />
            <div className="checklist-header">
              <div>
                <h3 className="checklist-title">Shopping list</h3>
                <p className="checklist-store">
                  {confirmedStore?.name} · {items.length} item{items.length !== 1 ? 's' : ''}
                </p>
              </div>
              <button className="checklist-close" onClick={() => setShowChecklist(false)}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="4" y1="4" x2="12" y2="12"/>
                  <line x1="12" y1="4" x2="4" y2="12"/>
                </svg>
              </button>
            </div>
            <div className="checklist-items">
              {items.map((item, i) => (
                <div key={item.id || i} className="checklist-row">
                  <span className="checklist-circle" />
                  <span className="checklist-name">{item.name}</span>
                  <span className="checklist-qty">×{item.quantity}</span>
                </div>
              ))}
            </div>
            <div className="checklist-footer">
              <p className="checklist-save-note">
                Save this list to reuse it for future shops (Optional)
              </p>
              {onSave && (
                <button
                  className="checklist-save-btn"
                  onClick={() => { setShowChecklist(false); onSave(); }}
                >
                  Save list
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── Store compare sheet ── */}
      {showCompare && (
        <StoreCompareSheet
          items={items}
          stores={stores}
          onClose={() => setShowCompare(false)}
        />
      )}

    </>
  );
}

export default ShoppingList;
